/**
 * Notification Config (Frontend)
 * FindMyLawyer – Lawyer Portal
 *
 * Single source of truth for notification UI metadata.
 * Maps notification type → { icon component name, colors, label }.
 * Used by NotificationCard and NotificationBadge.
 */

// Icon mapping (uses Lucide icon names as strings — resolved in components)
export const NOTIFICATION_CONFIG = {
  BOOKING: {
    icon:     "Calendar",
    color:    "#3b82f6",   // blue-500
    bgColor:  "#eff6ff",   // blue-50
    dotColor: "#1d4ed8",   // blue-700
    label:    "Booking",
  },
  BOOKING_ACCEPTED: {
    icon:     "CheckCircle",
    color:    "#22c55e",   // green-500
    bgColor:  "#f0fdf4",   // green-50
    dotColor: "#15803d",
    label:    "Booking",
  },
  BOOKING_CANCELLED: {
    icon:     "XCircle",
    color:    "#ef4444",   // red-500
    bgColor:  "#fef2f2",   // red-50
    dotColor: "#b91c1c",
    label:    "Booking",
  },
  MEETING: {
    icon:     "Video",
    color:    "#8b5cf6",   // violet-500
    bgColor:  "#f5f3ff",   // violet-50
    dotColor: "#6d28d9",
    label:    "Meeting",
  },
  CHAT: {
    icon:     "MessageCircle",
    color:    "#06b6d4",   // cyan-500
    bgColor:  "#ecfeff",   // cyan-50
    dotColor: "#0e7490",
    label:    "Message",
  },
  DOCUMENT: {
    icon:     "FileText",
    color:    "#f59e0b",   // amber-500
    bgColor:  "#fffbeb",   // amber-50
    dotColor: "#b45309",
    label:    "Document",
  },
  VERIFICATION: {
    icon:     "ShieldCheck",
    color:    "#6366f1",   // indigo-500
    bgColor:  "#eef2ff",   // indigo-50
    dotColor: "#4338ca",
    label:    "Verification",
  },
  SUBSCRIPTION: {
    icon:     "Star",
    color:    "#f97316",   // orange-500
    bgColor:  "#fff7ed",   // orange-50
    dotColor: "#c2410c",
    label:    "Subscription",
  },
  PAYMENT: {
    icon:     "IndianRupee",
    color:    "#10b981",   // emerald-500
    bgColor:  "#ecfdf5",   // emerald-50
    dotColor: "#065f46",
    label:    "Payment",
  },
  EARNINGS: {
    icon:     "Wallet",
    color:    "#10b981",   // emerald-500
    bgColor:  "#ecfdf5",
    dotColor: "#065f46",
    label:    "Earnings",
  },
  SUPPORT: {
    icon:     "Headphones",
    color:    "#64748b",   // slate-500
    bgColor:  "#f8fafc",   // slate-50
    dotColor: "#334155",
    label:    "Support",
  },
  SYSTEM: {
    icon:     "Info",
    color:    "#64748b",
    bgColor:  "#f8fafc",
    dotColor: "#334155",
    label:    "System",
  },
  AI: {
    icon:     "Bot",
    color:    "#a855f7",   // purple-500
    bgColor:  "#faf5ff",   // purple-50
    dotColor: "#7e22ce",
    label:    "LEX.ai",
  },
};

// ── Priority → color indicator ────────────────────────────────────────────────
export const PRIORITY_COLORS = {
  CRITICAL: { bg: "#fef2f2", border: "#fca5a5", dot: "#dc2626", label: "Critical" },
  HIGH:     { bg: "#fff7ed", border: "#fdba74", dot: "#ea580c", label: "High"     },
  MEDIUM:   { bg: "#f0f9ff", border: "#7dd3fc", dot: "#0284c7", label: "Medium"   },
  LOW:      { bg: "#f8fafc", border: "#e2e8f0", dot: "#94a3b8", label: "Low"      },
};

// ── Filter tab definitions ────────────────────────────────────────────────────
export const FILTER_TABS = [
  { id: "ALL",          label: "All",          type: null               },
  { id: "UNREAD",       label: "Unread",       status: "UNREAD"         },
  { id: "BOOKING",      label: "Bookings",     type: "BOOKING"          },
  { id: "CHAT",         label: "Messages",     type: "CHAT"             },
  { id: "MEETING",      label: "Meetings",     type: "MEETING"          },
  { id: "PAYMENT",      label: "Payments",     type: "PAYMENT"          },
  { id: "VERIFICATION", label: "Verification", type: "VERIFICATION"     },
  { id: "SYSTEM",       label: "System",       type: "SYSTEM"           },
];

// ── Resolve action URL from source_type + source_id ──────────────────────────
// Frontend decides the URL — no hardcoded URLs stored in backend.
export function resolveActionUrl(sourceType, sourceId, notification) {
  if (!sourceType) return null;

  const routes = {
    BOOKING:  "/lawyer/bookings",
    CHAT:     "/lawyer/chat",
    DOCUMENT: "/lawyer/cases",
    MEETING:  "/lawyer/schedule",
    CASE:     "/lawyer/cases",
    AI:       "/lawyer/ai-help",
    SUPPORT:  "/lawyer/settings",
    PAYMENT:  "/lawyer/payments",
    EARNINGS: "/lawyer/payments",
    SUBSCRIPTION: "/lawyer/settings",
  };

  return routes[sourceType] || null;
}

// ── Get notification config (with safe fallback) ──────────────────────────────
export function getNotificationConfig(type) {
  return NOTIFICATION_CONFIG[type] || {
    icon:     "Bell",
    color:    "#64748b",
    bgColor:  "#f8fafc",
    dotColor: "#334155",
    label:    "Notification",
  };
}
